import React, { useState, useEffect } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { firebase } from 'common';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';

export default function ScanAnalytics() {
    const { i18n } = useTranslation();
    const isRTL = i18n.dir();
    const settings = useSelector(state => state.settingsdata.settings);
    const [serialNumbers, setSerialNumbers] = useState({});
    const [qrCodes, setQrCodes] = useState({});
    const [vehicleRegistrations, setVehicleRegistrations] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        const firestoreDb = getFirestore(firebase.app, 'scanme');
        let loaded = { serialNumbers: false, qrCodes: false, vehicleRegistrations: false };
        const markLoaded = (key) => {
            loaded[key] = true;
            if (loaded.serialNumbers && loaded.qrCodes && loaded.vehicleRegistrations) setLoading(false);
        };

        // onSnapshot gives a QuerySnapshot (list of docs) - rebuild the {[id]: data} shape
        // the aggregation logic below expects, same as the RTDB snapshot.val() used to provide.
        const toKeyedObject = (querySnapshot) => {
            const obj = {};
            querySnapshot.forEach(doc => { obj[doc.id] = doc.data(); });
            return obj;
        };

        const unsubSerials = onSnapshot(collection(firestoreDb, 'serialNumbers'), (snapshot) => {
            setSerialNumbers(toKeyedObject(snapshot));
            markLoaded('serialNumbers');
        }, () => markLoaded('serialNumbers'));

        const unsubQr = onSnapshot(collection(firestoreDb, 'qrCodes'), (snapshot) => {
            setQrCodes(toKeyedObject(snapshot));
            markLoaded('qrCodes');
        }, () => markLoaded('qrCodes'));

        const unsubRegs = onSnapshot(collection(firestoreDb, 'vehicleRegistrations'), (snapshot) => {
            setVehicleRegistrations(toKeyedObject(snapshot));
            markLoaded('vehicleRegistrations');
        }, () => markLoaded('vehicleRegistrations'));

        return () => {
            unsubSerials();
            unsubQr();
            unsubRegs();
        };
    }, []);

    // Scan counters can land on any of three nodes depending on when in the serial's
    // lifecycle (generated-only, seeded QR record, or a real self-registration) it was
    // scanned, so a serial's true lifetime counts are the sum across all of them.
    const rowsBySerial = {};

    const getRow = (serialNumber) => {
        if (!rowsBySerial[serialNumber]) {
            rowsBySerial[serialNumber] = {
                serialNumber,
                vehicleNumber: '',
                ownerName: '',
                activated: false,
                scansWhileInactive: 0,
                scansWhileActive: 0,
                lastScannedAt: 0
            };
        }
        return rowsBySerial[serialNumber];
    };

    const applyScanFields = (row, data) => {
        row.scansWhileInactive += data.scansWhileInactive || 0;
        row.scansWhileActive += data.scansWhileActive || 0;
        if (data.lastScannedAt && data.lastScannedAt > row.lastScannedAt) row.lastScannedAt = data.lastScannedAt;
    };

    Object.entries(serialNumbers).forEach(([serial, data]) => {
        const row = getRow(serial);
        applyScanFields(row, data);
    });

    Object.entries(qrCodes).forEach(([serial, data]) => {
        const row = getRow(serial);
        applyScanFields(row, data);
        row.vehicleNumber = data.vehicleNumber || row.vehicleNumber;
        row.ownerName = data.ownerName || row.ownerName;
        if (data.activated === true) row.activated = true;
    });

    Object.values(vehicleRegistrations).forEach((data) => {
        if (!data.serialNumber) return;
        const row = getRow(data.serialNumber);
        applyScanFields(row, data);
        row.vehicleNumber = data.vehicleNumber || row.vehicleNumber;
        row.ownerName = data.name || row.ownerName;
        if (data.activated === true) row.activated = true;
    });

    const tableData = Object.values(rowsBySerial).sort((a, b) => b.lastScannedAt - a.lastScannedAt);

    const columns = [
        { title: 'Serial Number', field: 'serialNumber', editable: 'never' },
        { title: 'Vehicle Plate', field: 'vehicleNumber', editable: 'never', render: rowData => rowData.vehicleNumber || '—' },
        { title: 'Owner Name', field: 'ownerName', editable: 'never', render: rowData => rowData.ownerName || '—' },
        {
            title: 'Status',
            field: 'activated',
            editable: 'never',
            render: rowData => rowData.activated ? 'Activated' : 'Not activated'
        },
        { title: 'Scans (Inactive)', field: 'scansWhileInactive', editable: 'never', type: 'numeric' },
        { title: 'Scans (Active)', field: 'scansWhileActive', editable: 'never', type: 'numeric' },
        {
            title: 'Total Scans',
            field: 'totalScans',
            editable: 'never',
            type: 'numeric',
            render: rowData => rowData.scansWhileInactive + rowData.scansWhileActive
        },
        {
            title: 'Last Scanned',
            field: 'lastScannedAt',
            editable: 'never',
            defaultSort: 'desc',
            render: rowData => rowData.lastScannedAt ? moment(rowData.lastScannedAt).format('lll') : 'Never'
        },
    ];

    return (
        loading ? <CircularLoading /> :
            <div style={{ backgroundColor: 'var(--bg-main)', minHeight: '100vh', padding: '24px' }}>
                <MaterialTable
                    title="QR Scan Analytics"
                    columns={columns}
                    style={{
                        direction: isRTL === "rtl" ? "rtl" : "ltr",
                        borderRadius: "20px",
                        boxShadow: "var(--shadow)",
                        padding: "0",
                        backgroundColor: "var(--bg-card)",
                        backdropFilter: "blur(12px)",
                        border: "var(--card-border)",
                        overflow: "hidden"
                    }}
                    data={tableData}
                    onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
                    options={{
                        pageSize: 10,
                        pageSizeOptions: [10, 15, 20],
                        exportCsv: (columns, data) => {
                            let hArray = [];
                            const headerRow = columns.map(col => {
                                hArray.push(col.field);
                                return col.title;
                            });
                            const dataRows = data.map((row) => {
                                row.totalScans = row.scansWhileInactive + row.scansWhileActive;
                                row.status = row.activated ? 'Activated' : 'Not activated';
                                let dArr = [];
                                for (let i = 0; i < hArray.length; i++) {
                                    dArr.push(row[hArray[i]]);
                                }
                                return Object.values(dArr);
                            })
                            const delimiter = ",";
                            const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
                            const csvFileName = 'ScanAnalytics.csv';
                            downloadCsv(csvContent, csvFileName);
                        },
                        exportButton: {
                            csv: settings.AllowCriticalEditsAdmin,
                            pdf: false,
                        },
                        maxColumnSort: "all_columns",
                        rowStyle: rowData => ({
                            backgroundColor: (selectedRow === rowData.tableData.id) ? "var(--table-row-selected)" : "var(--table-row-bg)",
                            borderBottom: "var(--table-border)",
                            fontSize: "14px",
                            color: "var(--text-main)"
                        }),
                        headerStyle: {
                            position: "sticky",
                            top: "0px",
                            background: "var(--table-header-bg)",
                            color: "var(--text-light)",
                            textAlign: "center",
                            fontWeight: "700",
                            fontFamily: 'Inter, sans-serif',
                            border: "none",
                            height: "56px",
                            borderBottom: "var(--card-border)"
                        },
                        cellStyle: {
                            borderBottom: "var(--table-border)",
                            textAlign: "center",
                            padding: "14px",
                            color: "var(--text-main)"
                        },
                        actionsColumnIndex: -1,
                    }}
                />
            </div>
    );
}
